import React from 'react';

/**
 * Token-driven Recharts theme.
 * Use:
 *   <CartesianGrid {...chartGrid} />
 *   <XAxis {...chartAxis} />
 *   <YAxis {...chartAxis(true)} />   // numeric axis (right side, hidden)
 *   <Tooltip content={<ChartTooltip />} />
 */

export const CHART_PALETTE = [
  'var(--accent)',
  'var(--success)',
  'var(--warning)',
  'var(--danger)',
  '#0EA5E9',  // sky
  '#A855F7',  // purple
  '#F97316',  // orange
  '#14B8A6',  // teal
] as const;

const cssVar = (name: string, fallback: string) => {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
};

export const chartGrid = {
  strokeDasharray: '3 4',
  vertical: false,
  stroke: 'var(--border)' as string,
};

export const chartAxisStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  fill: 'var(--fg-3)',
};

export const chartAxisProps = {
  axisLine: false,
  tickLine: false,
  tick: { fill: 'var(--fg-3)', fontSize: 11, fontFamily: 'var(--font-mono)' },
  style: chartAxisStyle,
} as const;

/**
 * Custom Tooltip — drop-in replacement for default recharts tooltip.
 * Pass via <Tooltip content={<ChartTooltip />} /> and recharts will hydrate
 * `active`, `payload`, `label` props at render time.
 */
interface ChartTooltipProps {
  active?: boolean;
  payload?: Array<{
    name?: string;
    value?: number | string;
    color?: string;
    dataKey?: string;
    payload?: Record<string, unknown>;
  }>;
  label?: React.ReactNode;
  /** Optional value formatter — applied to each payload entry. */
  formatter?: (value: number | string, name?: string) => React.ReactNode;
  /** Show inner-row units (e.g. "clicks"). */
  unitFor?: (name?: string) => string | undefined;
}

export const ChartTooltip: React.FC<ChartTooltipProps> = ({
  active,
  payload,
  label,
  formatter,
  unitFor
}) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div
      role="tooltip"
      style={{
        background: 'var(--surface-elev, var(--surface))',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-2)',
        boxShadow: 'var(--shadow-md)',
        padding: '8px 11px',
        fontSize: 12,
        color: 'var(--fg-1)',
        minWidth: 130,
        maxWidth: 280,
        pointerEvents: 'none',
        fontFamily: 'var(--font-sans)'
      }}
    >
      {label != null && (
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            color: 'var(--fg-3)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            marginBottom: 6,
          }}
        >
          {label}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {payload.map((entry, idx) => {
          const v = entry.value;
          const out = formatter && v != null ? formatter(v, entry.name) : v;
          const unit = unitFor && entry.name ? unitFor(entry.name) : undefined;
          return (
            <div
              key={`${entry.dataKey ?? idx}-${entry.name ?? idx}`}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--fg-2)' }}>
                <span
                  style={{
                    width: 8, height: 8, borderRadius: 2,
                    background: entry.color || 'var(--accent)',
                    flex: '0 0 8px'
                  }}
                  aria-hidden="true"
                />
                {entry.name}
              </span>
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontVariantNumeric: 'tabular-nums',
                  color: 'var(--fg-1)',
                  fontWeight: 600
                }}
              >
                {out}{unit ? ` ${unit}` : ''}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/** Decorative shared <linearGradient> defs for area/line fills. */
export const ChartGradientDefs: React.FC<{ id?: string; color?: string }> = ({
  id = 'chart-fade-accent',
  color = 'var(--accent)'
}) => (
  <defs>
    <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%"   stopColor={color} stopOpacity={0.35} />
      <stop offset="100%" stopColor={color} stopOpacity={0}    />
    </linearGradient>
  </defs>
);

// Expose for non-React callers (e.g. table cell tinting)
export function getChartColors(): string[] {
  return [
    cssVar('--accent', '#4F46E5'),
    cssVar('--success', '#16A34A'),
    cssVar('--warning', '#D97706'),
    cssVar('--danger',  '#DC2626'),
  ];
}
